// Port of lib/core/mfa_gate.dart — the two-factor (TOTP) rules shared with
// the Flutter client (.claude/qa/mfa-contract.md §3, §4b, §5). Pure functions
// only, so both suites run the same vectors
// (test/fixtures/mfa_gate_vectors.json, see mfaGate.test.ts).

/** The top-level screen RootRouter mounts (contract §3.1). */
export type AuthRoute =
  | 'welcome'
  | 'emailConfirmed'
  /** The two-factor challenge after a password sign-in / restored session. */
  | 'mfaChallenge'
  /** The same challenge, opened from a password-reset link. */
  | 'mfaChallengeReset'
  | 'resetPassword'
  /** Profile loading / error / onboarding / the app (unchanged). */
  | 'app';

/** `needsMfa := currentLevel == aal1 && nextLevel == aal2` (contract §0). */
export function needsMfa(currentLevel: string | null | undefined, nextLevel: string | null | undefined): boolean {
  return currentLevel === 'aal1' && nextLevel === 'aal2';
}

/** Router order of contract §3.1. The challenge sits above the recovery
 *  branch so a reset link of an enrolled user asks for the code first, and
 *  above the profile so nothing reads data at aal1. */
export function resolveAuthRoute(a: {
  hasSession: boolean;
  currentLevel: string | null | undefined;
  nextLevel: string | null | undefined;
  recovery: boolean;
  emailJustConfirmed: boolean;
}): AuthRoute {
  if (!a.hasSession) return 'welcome';
  if (a.emailJustConfirmed) return 'emailConfirmed';
  if (needsMfa(a.currentLevel, a.nextLevel)) return a.recovery ? 'mfaChallengeReset' : 'mfaChallenge';
  if (a.recovery) return 'resetPassword';
  return 'app';
}

function base64UrlDecode(s: string): string {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  const bin = atob(padded);
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** The `aal` claim of an access token, or null when it can't be read. */
export function aalOfAccessToken(accessToken: string | null | undefined): string | null {
  if (!accessToken) return null;
  try {
    const parts = accessToken.split('.');
    if (parts.length < 2) return null;
    const map: unknown = JSON.parse(base64UrlDecode(parts[1] ?? ''));
    if (map && typeof map === 'object' && typeof (map as { aal?: unknown }).aal === 'string') {
      return (map as { aal: string }).aal;
    }
  } catch {
    // Not a JWT we can read: treat as unknown.
  }
  return null;
}

/** The shape of a supabase-js Session this module needs (kept structural so
 *  the rules stay testable without the SDK). */
export interface SessionLike {
  access_token: string;
  user?: { factors?: { status?: string }[] | null } | null;
}

/** (currentLevel, nextLevel) of [session], computed like GoTrue's
 *  getAuthenticatorAssuranceLevel (JWT `aal` + verified factors on the
 *  session's user), synchronously and without trusting a missing claim. */
export function assuranceLevels(session: SessionLike | null | undefined): { current: string | null; next: string | null } {
  if (!session) return { current: null, next: null };
  const current = aalOfAccessToken(session.access_token);
  const hasVerified = (session.user?.factors ?? []).some((f) => f.status === 'verified');
  return { current, next: hasVerified ? 'aal2' : current };
}

/** True while [session] belongs to an enrolled user who has not entered the
 *  code yet. Nothing may query PostgREST in that state (contract §3.1). */
export function sessionNeedsMfa(session: SessionLike | null | undefined): boolean {
  const l = assuranceLevels(session);
  return needsMfa(l.current, l.next);
}

/** Auth events after which the client may (re)load the user's data — the
 *  profile / settings / platform sync in App.tsx — provided the session no
 *  longer needs the code. MFA_CHALLENGE_VERIFIED ends the gate (§3.1). */
export function isDataLoadEvent(event: string | null | undefined): boolean {
  return event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'USER_UPDATED' || event === 'MFA_CHALLENGE_VERIFIED';
}

// ---------------------------------------------------------------------------
// Errors → actions (contract §3.4, §4b, §5)
// ---------------------------------------------------------------------------

export type MfaErrorAction =
  /** 42501 mfa_required: refresh the session, then the gate shows. */
  | 'refreshAndGate'
  /** 42501 admin_mfa_required: show the admin 2FA gate (no sign-out). */
  | 'adminGate'
  /** PGRST202: the server has no two-factor yet — hide the feature. */
  | 'capabilityOff'
  | 'wrongCode'
  /** mfa_challenge_expired / mfa_ip_address_mismatch: new challenge once. */
  | 'rechallenge'
  | 'tooMany'
  /** mfa_factor_not_found: refresh the session and re-evaluate. */
  | 'refreshSession'
  /** stale unverified factors: remove them, retry once. */
  | 'cleanupRetry'
  /** insufficient_aal: show the challenge, then retry. */
  | 'challenge'
  | 'hideFeature'
  | 'generic'
  /** Not a two-factor error: the caller's usual handling applies. */
  | 'none';

/** Maps an error to its action. [source] is `postgrest` or `gotrue`. */
export function mfaErrorAction(a: {
  source: 'postgrest' | 'gotrue';
  code?: string | null;
  message?: string | null;
  status?: number | null;
}): MfaErrorAction {
  const { source, code, message, status } = a;
  if (source === 'postgrest') {
    if (code === 'PGRST202') return 'capabilityOff';
    if (code === '42501' && message === 'mfa_required') return 'refreshAndGate';
    if (code === '42501' && message === 'admin_mfa_required') return 'adminGate';
    return 'none';
  }
  switch (code) {
    case 'mfa_verification_failed':
      return 'wrongCode';
    case 'mfa_challenge_expired':
    case 'mfa_ip_address_mismatch':
      return 'rechallenge';
    case 'over_request_rate_limit':
      return 'tooMany';
    case 'mfa_factor_not_found':
      return 'refreshSession';
    case 'mfa_factor_name_conflict':
    case 'too_many_enrolled_mfa_factors':
      return 'cleanupRetry';
    case 'insufficient_aal':
      return 'challenge';
    case 'mfa_totp_enroll_not_enabled':
    case 'mfa_totp_verify_not_enabled':
      return 'hideFeature';
    case 'mfa_verification_rejected':
      return 'generic';
  }
  if (status === 429) return 'tooMany';
  return 'generic';
}

/** Reads PostgREST's error body (`{"code": "42501", "message": …}`) and
 *  returns the action for it, or 'none'. */
export function mfaActionFromPostgrestBody(body: string): MfaErrorAction {
  try {
    const map: unknown = JSON.parse(body);
    if (!map || typeof map !== 'object' || Array.isArray(map)) return 'none';
    const m = map as { code?: unknown; message?: unknown };
    return mfaErrorAction({
      source: 'postgrest',
      code: m.code == null ? null : String(m.code),
      message: m.message == null ? null : String(m.message),
    });
  } catch {
    return 'none';
  }
}

/** The message an admin sees for an error of admin_reset_user_mfa /
 *  admin_user_mfa_status (contract §5) — the ARB key itself. */
export type AdminMfaError =
  | 'adminMfaErrSelf'
  | 'adminMfaErrNote'
  | 'adminMfaErrPaymentRequired'
  | 'adminMfaErrNoPayment'
  | 'adminMfaErrBooking'
  | 'adminMfaErrNoFactors'
  | 'adminMfaErrUserGone'
  | 'adminMfaErrStepUp'
  | 'adminMfaErrGeneric';

export function adminMfaErrorFor(_code: string | null | undefined, message: string | null | undefined): AdminMfaError {
  switch (message) {
    case 'self_reset_not_allowed':
      return 'adminMfaErrSelf';
    case 'note_required':
      return 'adminMfaErrNote';
    case 'payment_reference_required':
      return 'adminMfaErrPaymentRequired';
    case 'no_payment_on_record':
      return 'adminMfaErrNoPayment';
    case 'booking_not_found_for_user':
      return 'adminMfaErrBooking';
    case 'no_mfa_factors':
      return 'adminMfaErrNoFactors';
    case 'user_not_found':
      return 'adminMfaErrUserGone';
    case 'mfa_required':
    case 'admin_mfa_required':
      return 'adminMfaErrStepUp';
  }
  return 'adminMfaErrGeneric';
}

/** Local check before calling admin_reset_user_mfa; null when the input may
 *  be sent. The server checks again — this only spares a round trip. */
export function adminMfaResetInputError(a: {
  method: string;
  bookingId?: number | null;
  reference?: string | null;
  note?: string | null;
}): AdminMfaError | null {
  const ref = (a.reference ?? '').trim();
  const note = (a.note ?? '').trim();
  if (a.method === 'payment_reference') {
    if (a.bookingId == null && ref.length < 3) return 'adminMfaErrPaymentRequired';
    return null;
  }
  if (a.method === 'account_email') {
    if (note.length < 8) return 'adminMfaErrNote';
    return null;
  }
  return 'adminMfaErrGeneric';
}

// ---------------------------------------------------------------------------
// mfa_status() (migration 180; admin_mfa_required from 182)
// ---------------------------------------------------------------------------

export interface MfaStatus {
  hasVerifiedFactor: boolean;
  mfaRequired: boolean;
  /** Migration 182: the caller is an admin below aal2. Absent before → false. */
  adminMfaRequired: boolean;
  aal: string | null;
}

export function parseMfaStatus(j: Record<string, unknown>): MfaStatus {
  return {
    hasVerifiedFactor: j.has_verified_factor === true,
    mfaRequired: j.mfa_required === true,
    adminMfaRequired: j.admin_mfa_required === true,
    aal: typeof j.aal === 'string' ? j.aal : null,
  };
}

/** The manual key in groups of four ("JBSW Y3DP EHPK 3PXP"). */
export function groupKey(secret: string): string {
  const clean = secret.replace(/ /g, '');
  return clean.replace(/(.{4})(?=.)/g, '$1 ');
}
